import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import { selectLocale } from 'models/localization';
import ReactHtmlParser from 'react-html-parser';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { FETCH_ASSETS_URL } from 'appenv';

const EventXtraLogo = `${FETCH_ASSETS_URL}/assets/eventx-logo-white.png`;

interface FooterLogo {
  src: string;
  width: number;
  alt?: string;
}

const RootContainer = styled.div<{ textColor: string }>`
  min-width: 100px;
  border-radius: 4px;
  padding: 4px 8px;
  display: flex;
  left: 0;
  bottom: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ textColor }) => textColor};

  @media (min-width: 480px) {
    flex-direction: row;
  }
`;

const StyledText = styled.div`
  flex: 0 0 auto;
  font-style: normal;
  font-weight: normal;
  font-size: 11px;
  line-height: 14px;
  white-space: nowrap;
  padding-bottom: 8px;

  @media (min-width: 480px) {
    margin-right: 16px;
    padding-bottom: 0;
  }
`;

const StyledAnchor = styled.a`
  flex: 0 0 auto;
  line-height: 50px;
  font-family: Lato, "sans-serif";
  text-decoration: none;
`;

const StyledImg = styled.img<{ maxWidth: number }>`
  max-width: ${({ maxWidth }) => maxWidth}px;
  max-height: 20px;
  height: auto;

  &:not(:last-child) {
    margin-right: 8px;
  }
`;

const FooterLogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

const DEFAULT_FOOTER_LOGOS: FooterLogo[] = [
  {
    src: EventXtraLogo,
    alt: 'EventX',
    width: 70,
  },
];

const EventXtraBranding = ({ url }: {
  url: string;
}) => {
  const { footer } = useSelector((state: any) => state.eventConfigs);
  const locale = useSelector(selectLocale);
  const footerTextColor = footer?.textColor || '#fff';
  const footerHeaderText = findAvailableLocale(footer?.header, locale) || 'Made by';
  const footerLogos: FooterLogo[] = useMemo(() => {
    const logos = findAvailableLocale(footer?.logos, locale) as FooterLogo[];

    if (isEmpty(logos)) return DEFAULT_FOOTER_LOGOS;
    return logos;
  }, [footer?.logos, locale]);

  return (
    <RootContainer
      as={StyledAnchor}
      href={url}
      elevation="4"
      target="_blank"
      rel="noopener noreferrer"
      title={footerHeaderText}
      textColor={footerTextColor}
    >
      <StyledText>
        {ReactHtmlParser(footerHeaderText)}
      </StyledText>
      <FooterLogoContainer>
        {footerLogos.map(({ src, alt, width }, index) => (
          <StyledImg key={index} src={src} maxWidth={width} alt={alt} />
        ))}
      </FooterLogoContainer>
    </RootContainer>
  );
};

export default EventXtraBranding;
